import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import { useDeleteBookingMutation } from '../Service/bookingsApi';
import {
  BookingNavigationState,
  BookingSearchFilters,
  defaultBookingSearchFilters,
} from '../Types/booking.types';

import { BookingsRoutes } from '../../../Router/path';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import ActionButtons from '../../Shared/Components/ActionButtons';
import DataTable, { ColumnConfig, ColumnType } from '../../Shared/Components/DataTable';
import Status from '../../Shared/Components/Status/Status';
import CancelledBookingChip, { CANCELLED_ROW_SX } from '../../Shared/Components/CancelledBookingChip/CancelledBookingChip';
import {
  useCancelBookingMutation,
  useGetBookingSearchV2ListQuery,
  usePartialUpdateBookingMutation,
} from '../Service/extendedBookingsApi';
import { formatDate } from '../../Shared/Helpers/formatDate';
import BookingSearchForm from '../Components/BookingSearchForm';
import { useDeleteSelectedItems } from '../../Shared/Hooks/useDeleteSelectedItems';
import { useCreateRedirect } from '../../Shared/Hooks/useCreateRedirect';
import { mapBookingSearchFiltersToRequest } from '../Service/mapBookingSearchFiltersToRequest';
import { BookingSearchListItem } from '../Types/bookingSearchTypes';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { CANCEL_BOOKING_CONFIRMATION_TEXT } from '../Service/bookingCancellationApi.contract';

const toComparableDate = (value?: string): number | null => {
  if (!value) {
    return null;
  }

  const normalized = value.slice(0, 10);
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const Bookings = () => {
  const location = useLocation();
  const navigationState = (location.state as BookingNavigationState | null) ?? null;
  const [filters, setFilters] = useState<BookingSearchFilters>(
    navigationState?.restoredFilters ?? defaultBookingSearchFilters
  );
  const [page, setPage] = useState(navigationState?.restoredPage ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(navigationState?.restoredRowsPerPage ?? 10);
  const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedRow, setEditedRow] = useState<Partial<BookingSearchListItem>>({});
  const [bookingIdToCancel, setBookingIdToCancel] = useState<number | null>(null);

  const queryArgs = useMemo(
    () => mapBookingSearchFiltersToRequest(filters, { page, pageSize: rowsPerPage }),
    [filters, page, rowsPerPage]
  );

  const { data, isLoading, isError, error } = useGetBookingSearchV2ListQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
  });

  const [deleteBooking, statusDelete] = useDeleteBookingMutation();
  const [updatePartialBooking] = usePartialUpdateBookingMutation();
  const [cancelBooking, { isLoading: isCancellingBooking }] = useCancelBookingMutation();
  const navigate = useNavigate();

  const isInvalidParticipantsValue = (value?: number) =>
    value !== undefined && (!Number.isInteger(value) || value < 1);

  const isInvalidDateRange = (dateFrom?: string, dateTo?: string) => {
    const fromTimestamp = toComparableDate(dateFrom);
    const toTimestamp = toComparableDate(dateTo);

    if (fromTimestamp === null || toTimestamp === null) {
      return false;
    }

    return fromTimestamp > toTimestamp;
  };

  const handleDeleteSelected = useDeleteSelectedItems<number>(
    selectedBookingIds,
    (id) => deleteBooking(id).unwrap(),
    () => setSelectedBookingIds([])
  );

  const handleCancelBooking = async () => {
    if (!bookingIdToCancel) {
      return;
    }

    const targetBookingId = bookingIdToCancel;
    setBookingIdToCancel(null);
    setEditingRowId(null);
    setEditedRow({});

    try {
      await cancelBooking(targetBookingId).unwrap();
      toast.success('Termin został anulowany.');
    } catch (cancelError) {
      toast.error(extractApiErrorMessage(cancelError, 'Nie udało się anulować terminu.'));
    }
  };

  const handleCreate = useCreateRedirect(BookingsRoutes.create);

  const columns: ColumnConfig<BookingSearchListItem>[] = useMemo(
    () => [
      {
        key: 'bookingDateFrom',
        label: 'Data rezerwacji',
        editable: true,
        type: ColumnType.date,
        render: (row) => formatDate(row.bookingDateFrom),
      },
      {
        key: 'bookingDateTo',
        label: 'Data zakończenia rezerwacji',
        editable: true,
        type: ColumnType.date,
        render: (row) => formatDate(row.bookingDateTo),
      },
      { key: 'offerName', label: 'Nazwa Oferty' },
      {
        key: 'companyName',
        label: 'Firma',
        render: (row) => row.companyName || '-',
      },
      {
        key: 'searchMatches',
        label: 'Dopasowano po',
        render: (row) =>
          row.searchMatches.length > 0 ? (
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {row.searchMatches.map((match) => (
                <Typography
                  component="span"
                  key={`${row.id}-${match.field}`}
                  variant="caption"
                  sx={{
                    px: 1,
                    py: 0.25,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  {match.label}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">-</Typography>
          ),
      },
      {
        key: 'numberOfParticipants',
        label: 'Uczestnicy',
        width: 110,
        editable: true,
        render: (row) => (
          <Box display="flex" justifyContent="center">
            {row.numberOfParticipants}
          </Box>
        ),
      },
      { key: 'price', label: 'Cena' },
      { key: 'oldPrice', label: 'Stara Cena' },
      {
        key: 'bookingStatusName',
        label: 'Status rezerwacji',
        render: (row) =>
          row.cancelledAtUtc ? (
            <CancelledBookingChip cancelledAtUtc={row.cancelledAtUtc} />
          ) : (
            row.bookingStatusName
          ),
      },
      {
        key: 'published',
        label: 'Opublikowana',
        width: 110,
        render: (row) => (
          <Box display="flex" justifyContent="center">
            <input type="checkbox" disabled checked={row.published} />
          </Box>
        ),
      },
    ],
    []
  );

  const handleEdit = (id: number) => {
    const editPath = BookingsRoutes.update.replace(':bookingId', id.toString());

    navigate(editPath, {
      state: {
        returnContext: {
          pathname: BookingsRoutes.root,
          label: 'Wróć do wyników wyszukiwania',
          filters: { ...filters },
          page,
          rowsPerPage,
        },
      } satisfies BookingNavigationState,
    });
  };

  return (
    <>
      <CustomCard
        icon={<BookOnlineIcon fontSize="large" color="primary" />}
        title="Rezerwacje"
        subheader="Zarządzaj rezerwacjami klientów"
        sx={{ mb: 4 }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Na tej stronie możesz dodawać, edytować i usuwać rezerwacje. Możesz również oznaczać
            rezerwacje jako opublikowane lub nie.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Skorzystaj z przycisków, aby dodać nową rezerwację lub usunąć wybrane pozycje.
          </Typography>
        </Box>

        <ActionButtons onCreate={handleCreate} onDeleteSelected={handleDeleteSelected} />
        <BookingSearchForm
          initialFilters={filters}
          onSearch={(nextFilters) => {
            setFilters(nextFilters);
            setPage(0);
          }}
        />
      </CustomCard>

      <CustomCard>
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          isError={isError}
          errorMessage={extractApiErrorMessage(error, 'Wystąpił błąd podczas ładowania danych.')}
          emptyMessage="Brak wyników dla podanych kryteriów"
          getRowId={(row) => row.id}
          getRowSx={(row) => (row.cancelledAtUtc ? CANCELLED_ROW_SX : undefined)}
          renderCustomActions={(row) =>
            row.cancelledAtUtc ? null : (
              <Tooltip title="Anuluj termin">
                <span>
                  <IconButton
                    size="small"
                    color="warning"
                    aria-label="Anuluj termin"
                    sx={{ p: 0.5 }}
                    disabled={isCancellingBooking || editingRowId !== null}
                    onClick={() => setBookingIdToCancel(row.id)}
                  >
                    <EventBusyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )
          }
          onEdit={handleEdit}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.totalCount ?? 0}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(0);
          }}
          selected={selectedBookingIds}
          onSelectionChange={setSelectedBookingIds}
          editingRowId={editingRowId}
          setEditingRowId={setEditingRowId}
          editedRow={editedRow}
          setEditedRow={setEditedRow}
          onInlineSave={async (row) => {
            if (isInvalidParticipantsValue(row.numberOfParticipants)) {
              throw new Error(
                'Nieprawidłowa liczba uczestników: wartość musi być liczbą całkowitą większą lub równą 1.'
              );
            }

            if (isInvalidDateRange(row.bookingDateFrom, row.bookingDateTo)) {
              throw new Error(
                'Nieprawidłowy zakres dat: data od nie może być późniejsza niż data do.'
              );
            }

            const originalRow = data?.items.find((item) => item.id === row.id);
            const patch: {
              id: number;
              numberOfParticipants?: number;
              bookingDateFrom?: string;
              bookingDateTo?: string;
            } = { id: row.id };

            if (!originalRow || row.numberOfParticipants !== originalRow.numberOfParticipants) {
              patch.numberOfParticipants = row.numberOfParticipants;
            }

            if (!originalRow || row.bookingDateFrom !== originalRow.bookingDateFrom) {
              patch.bookingDateFrom = row.bookingDateFrom;
            }

            if (!originalRow || row.bookingDateTo !== originalRow.bookingDateTo) {
              patch.bookingDateTo = row.bookingDateTo;
            }

            if (Object.keys(patch).length === 1) {
              return;
            }

            await updatePartialBooking(patch).unwrap();
          }}
        />
      </CustomCard>

      <Status statusDelete={statusDelete} />

      <Dialog
        open={bookingIdToCancel !== null}
        onClose={() => setBookingIdToCancel(null)}
        aria-labelledby="cancel-booking-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="cancel-booking-dialog-title">Anulowanie terminu</DialogTitle>
        <DialogContent>{CANCEL_BOOKING_CONFIRMATION_TEXT}</DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingIdToCancel(null)}>Wróć</Button>
          <Button onClick={handleCancelBooking} color="warning" variant="contained">
            Anuluj termin
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Bookings;
